/**
 * Modal Helper Utility for vue-final-modal v4
 *
 * Provides Options API-friendly wrapper around useModal() composable
 * to maintain consistency with the codebase's 100% Options API architecture.
 */

import { useModal, VueFinalModal, useModalSlot } from 'vue-final-modal';

/**
 * Creates a modal helper instance for Options API components
 *
 * @param {Component} component - The modal component to display
 * @param {Object} config - Configuration object
 * @param {Object} [config.props={}] - Props to pass to the modal component
 * @param {Object} [config.attrs={}] - Attributes for the modal container (styling, transitions, etc.)
 * @param {Object} [config.callbacks={}] - Lifecycle callbacks (onClosed, onBeforeClose, custom events)
 * @param {Object} [config.slots={}] - Slot content for the modal
 * @param {Boolean} [config.keepAlive=false] - Whether to keep modal alive after close
 * @returns {Object} - { open, close, destroy, patchOptions } methods
 *
 * @example
 * // In a component method:
 * this.modalInstance = createModalHelper(MyModal, {
 *   props: {
 *     title: 'Hello',
 *     data: this.myData
 *   },
 *   attrs: {
 *     class: 'vfm--adaptive',
 *     contentClass: 'vfm__content--adaptive'
 *   },
 *   callbacks: {
 *     onClosed: () => console.log('Modal closed'),
 *     onInterestsSaved: () => this.handleSave()
 *   }
 * });
 * this.modalInstance.open();
 */
export function createModalHelper(component, config = {}) {
  const {
    props = {},
    attrs = {},
    callbacks = {},
    slots = {},
    keepAlive = false
  } = config;

  // Separate modal lifecycle callbacks from content component callbacks
  // Modal lifecycle: onClosed, onOpened, onBeforeClose, onBeforeOpen
  // Content callbacks: custom events like onInterestsSaved
  const modalLifecycleEvents = ['onClosed', 'onOpened', 'onBeforeClose', 'onBeforeOpen'];
  const modalCallbacks = {};
  const contentCallbacks = {};

  Object.keys(callbacks).forEach(key => {
    const eventKey = key.startsWith('on')
      ? key
      : `on${key.charAt(0).toUpperCase()}${key.slice(1)}`;

    if (modalLifecycleEvents.includes(eventKey)) {
      // This is a VueFinalModal lifecycle event
      modalCallbacks[eventKey] = callbacks[key];
    } else {
      // This is a content component event
      contentCallbacks[eventKey] = callbacks[key];
    }
  });

  // Merge props and content callbacks for the content component
  const contentComponentAttrs = {
    ...props,
    ...contentCallbacks
  };

  // Create the modal instance using useModal from vue-final-modal v4
  // In v4, we pass VueFinalModal as the wrapper, and the content component goes in slots
  const modalInstance = useModal({
    component: VueFinalModal,
    attrs: {
      ...attrs,
      ...modalCallbacks  // Add modal lifecycle callbacks here
    },
    slots: {
      // Put the actual content component in the default slot
      default: useModalSlot({
        component,
        attrs: {
          ...contentComponentAttrs,
          // Pass modelValue to content component
          modelValue: true,
          // Handle when content component emits update:modelValue (e.g., close button clicked)
          'onUpdate:modelValue': (value) => {
            if (!value) {
              // Content component wants to close - trigger the modal close
              modalInstance.close();
            }
          }
        }
      }),
      ...slots
    },
    keepAlive
  });

  return {
    open: modalInstance.open,
    close: modalInstance.close,
    destroy: modalInstance.destroy,
    patchOptions: modalInstance.patchOptions
  };
}

/**
 * Creates a modal helper with standard app styling
 * Includes default transitions, click behaviors, and sizing
 *
 * @param {Component} component - The modal component
 * @param {Object} [config={}] - Partial config (props, callbacks)
 * @param {Object} [config.props] - Props to pass to modal
 * @param {Object} [config.callbacks] - Lifecycle callbacks
 * @param {Object} [config.attrs] - Additional attrs (merged with defaults)
 * @param {String} [config.maxWidth='900px'] - Max width of modal
 * @returns {Object} - { open, close, destroy, patchOptions } methods
 *
 * @example
 * this.modalInstance = createStandardModal(EditInterestsModal, {
 *   props: { topics: this.topics },
 *   callbacks: { onInterestsSaved: () => this.handleSave() }
 * });
 * this.modalInstance.open();
 */
export function createStandardModal(component, config = {}) {
  const maxWidth = config.maxWidth || '900px';

  const defaultAttrs = {
    // VueFinalModal container - center the modal
    class: 'vfm-modal-center',
    // Overlay styling - semi-transparent dark background
    overlayClass: 'vfm-overlay-bg',
    // Content wrapper styling - use size-constrained class for sizing
    contentClass: `vfm-content-wrapper vfm-content-size-${maxWidth.replace(/[^0-9]/g, '')}`,
    // Transitions
    overlayTransition: 'vfm-fade',
    contentTransition: 'vfm-fade',
    // Behavior
    clickToClose: true,
    escToClose: true,
    lockScroll: true
  };

  return createModalHelper(component, {
    ...config,
    attrs: {
      ...defaultAttrs,
      ...(config.attrs || {})
    }
  });
}

/**
 * Creates a modal helper for route-synchronized modals
 * Includes onClosed callback that navigates to specified route
 *
 * @param {Component} component - The modal component
 * @param {Object} router - Vue router instance
 * @param {String} closeRoute - Route to navigate to on close
 * @param {Object} [config={}] - Additional config (props, callbacks, attrs)
 * @returns {Object} - { open, close, destroy, patchOptions } methods
 *
 * @example
 * this.modalInstance = createRouteModal(
 *   SummarizeNewsModal,
 *   this.$router,
 *   '/',
 *   { props: { data: this.data } }
 * );
 */
export function createRouteModal(component, router, closeRoute, config = {}) {
  const existingCallbacks = config.callbacks || {};

  return createStandardModal(component, {
    ...config,
    callbacks: {
      ...existingCallbacks,
      onClosed: () => {
        // Execute any existing onClosed callback first
        if (existingCallbacks.onClosed) {
          existingCallbacks.onClosed();
        }
        // Then navigate
        router.push(closeRoute);
      }
    }
  });
}
